<template>
  <div class="excel-table" ref="table">
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th v-for="(col, index) in columns" :key="index">
            {{ col.title }}
            <div class="actions" v-if="index !== 0">
              <button type="button" @click="toggleSign(index)">+/-</button>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, rowIndex) in data" :key="rowIndex"
          :class="{ 'highlight': activeRow === rowIndex }"
          @keydown="handleKeydown($event, rowIndex)">
          <td class="gray-background">{{ rowIndex }}</td>
          <td v-for="(col, colIndex) in columns" :key="colIndex"
            :ref="el => { if (el) cells[rowIndex][colIndex] = el; }"
            :class="{ 'gray-background': colIndex === 0 }"
            @click="focusCell(rowIndex, colIndex)"
             :contenteditable="colIndex !== 0"
            @input="updateCellValue($event.target.innerText, rowIndex, colIndex)"
            @focus="startEditing"
            @blur="stopEditing">
            {{ row[col.key] }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  props: {
    columns: Array,
    data: Array
  },
  data() {
    return {
      activeRow: -1,
      activeCol: -1,
      cells: [],
      cellValues: [],
      isEditing: false
    };
  },
  created() {
    this.cells = [];
    for (let i = 0; i < this.data.length; i++) {
      this.cells.push(new Array(this.columns.length).fill(null));
    }
    this.updateCellValues();
  },
  mounted() {
    this.$nextTick(() => {
      if (this.cells && this.cells[0] && this.cells[0][0]) {
        this.focusCell(0, 0);
      }
    });
  },
  watch: {
    data() {
      this.updateCellValues();
    }
  },
  methods: {
    updateCellValues() {
      this.cellValues = this.data.map(row => Object.values(row));
    },
    focusCell(rowIndex, colIndex) {
      if (this.isEditing) {
        this.stopEditing();
      }

      this.activeRow = rowIndex;
      this.activeCol = colIndex;
      if (!this.cells[rowIndex] || !this.cells[rowIndex][colIndex]) {
        return;
      }

      this.$nextTick(() => {
        if (this.cells[rowIndex][colIndex]) {
          this.cells[rowIndex][colIndex].focus();
          this.isEditing = true;
        }
      });
    },
    handleKeydown(event, rowIndex) {
      const { key } = event;
      const rowCount = this.data.length;
      const colCount = this.columns.length;

      switch (key) {
        case 'ArrowRight':
          if (this.activeCol < colCount - 1) {
            this.focusCell(rowIndex, this.activeCol + 1);
          }
          break;
        case 'ArrowLeft':
          if (this.activeCol > 0) {
            this.focusCell(rowIndex, this.activeCol - 1);
          }
          break;
        case 'ArrowUp':
          if (this.activeRow > 0) {
            this.focusCell(this.activeRow - 1, this.activeCol);
          }
          break;
        case 'ArrowDown':
          if (this.activeRow < rowCount - 1) {
            this.focusCell(this.activeRow + 1, this.activeCol);
          }
          break;
        case 'Home':
          this.focusCell(rowIndex, 0);
          break;
        case 'End':
          this.focusCell(rowIndex, colCount - 1);
          break;
        case 'Enter':
          if (this.activeRow < rowCount - 1) {
            this.focusCell(this.activeRow + 1, this.activeCol);
          }
          break;
        case 'Shift+Enter':
          if (this.activeRow > 0) {
            this.focusCell(this.activeRow - 1, this.activeCol);
          }
          break;
        case 'F2':
          if (!this.$refs.table.contains(document.activeElement)) {
            this.cells[this.activeRow][this.activeCol].focus();
          }
          break;
      }
    },
    updateCellValue(value, rowIndex, colIndex) {
      this.cellValues[rowIndex][colIndex] = value;
    },
    startEditing() {
      this.isEditing = true;
    },
    stopEditing() {
      if (this.activeRow < 0 || this.activeCol < 0) {
        return;
      }

      this.isEditing = false;
      const newValue = this.cellValues[this.activeRow][this.activeCol];
      this.$emit('change', { row: this.activeRow, cell: this.columns[this.activeCol].key, value: newValue });
    },
    toggleSign(colIndex) {
      for (let i = 0; i < this.data.length; i++) {
        this.cellValues[i][colIndex] = - this.cellValues[i][colIndex];
        this.$emit('change', { row: i, cell: this.columns[colIndex].key, value: this.cellValues[i][colIndex] });
      }
    }
  }
};
</script>

<style scoped>
.excel-table {
  width: 100%;
  overflow-x: auto;
}

table {
  border-collapse: collapse;
  width: 100%;
}

th,
td {
  padding: 8px;
  text-align: left;
  border: 1px solid #ddd;
}

thead {
    position: sticky;
    top: 0;
    z-index: 1;
}
tr.highlight {
  background-color: #f2f2f2;
}

.gray-background, th {
  background-color: #e0e0e0;
  border: 1px solid #aaa;
}

.actions {
  display: none;
  position: absolute;
  margin-left: 8px;
}

th:hover .actions {
  display: inline-block;
}

</style>